import { useCallback, useRef, useEffect, useState } from 'react';

import {
  Send,
  Bot,
  User,
  Loader2,
  Plus,
  Trash2,
  MessageSquare,
  Brain,
  Wrench,
  ChevronUp,
  ChevronDown,
  CheckCircle,
  XCircle,
  Mic,
  Download,
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useChats, useChatMessages, useDeleteChat, useSendMessage } from '@/hooks/useChats';
import { useDictation } from '@/hooks/useDictation';
import { usePageTitle } from '@/hooks/usePageTitle';
import { generateDataPdf, type DataPdfPayload } from '@/lib/data-pdf';
import { useChatStore, type UIMessage, type ToolCallDisplay } from '@/stores/chat.store';

import type { ChatMessageItem } from '@repo/shared-types';

const SUGGESTIONS = [
  { emoji: '📊', text: 'How many calls were answered vs missed last week?' },
  { emoji: '🚗', text: 'Who selected a car recently but has no booking?' },
  { emoji: '⚙️', text: 'List configurations and campaigns' },
];

// Convert API messages to UI messages
const apiMessageToUIMessage = (msg: ChatMessageItem): UIMessage => ({
  id: msg.id,
  role: msg.role === 'USER' ? 'user' : 'assistant',
  content: msg.content,
  timestamp: new Date(msg.created_at),
  toolCalls:
    msg.tool_calls?.map((tc) => ({
      tool_name: tc.tool_name,
      success: tc.result.success ?? true,
      formatted_text: tc.formatted_text ?? `The ${tc.tool_name} tool was executed.`,
    })) || undefined,
});

// Tool calls dropdown component
const ToolCallsDropdown = ({ toolCalls }: { toolCalls: ToolCallDisplay[] }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (toolCalls.length === 0) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="h-auto gap-2 px-2 py-1 text-xs">
          <Wrench className="h-3 w-3" />
          {toolCalls.length} tool{toolCalls.length > 1 ? 's' : ''} executed
          {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-muted-foreground/20 mt-2 space-y-2 border-l-2 pl-3">
        {toolCalls.map((tc, i) => (
          <div key={i} className="flex gap-2 text-xs">
            {tc.success ? (
              <CheckCircle className="mt-0.5 h-3 w-3 shrink-0 text-green-500" />
            ) : (
              <XCircle className="mt-0.5 h-3 w-3 shrink-0 text-red-500" />
            )}
            <span
              className={`whitespace-pre-wrap ${tc.success ? 'text-muted-foreground' : 'text-red-600 dark:text-red-400'}`}
            >
              {tc.formatted_text}
            </span>
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

// Collapsible reasoning (the model's streamed thinking) for a completed message
const ReasoningDropdown = ({ text }: { text: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  if (!text) return null;
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="h-auto gap-2 px-2 py-1 text-xs">
          <Brain className="h-3 w-3" />
          Reasoning
          {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-muted-foreground/20 text-muted-foreground mt-2 border-l-2 pl-3 text-xs whitespace-pre-wrap">
        {text}
      </CollapsibleContent>
    </Collapsible>
  );
};

// Welcome section component — centered empty-state design with suggestion chips
interface WelcomeSectionProps {
  onSuggestionClick: (suggestion: string) => void;
  disabled: boolean;
}

const WelcomeSection = ({ onSuggestionClick, disabled }: WelcomeSectionProps) => (
  <div className="flex min-h-full items-center justify-center">
    <div className="flex max-w-md flex-col items-center space-y-6 text-center">
      <div className="bg-muted flex h-14 w-14 items-center justify-center rounded-full">
        <Bot className="h-7 w-7" />
      </div>
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">👋 Hi there!</h3>
        <p className="text-muted-foreground text-sm">
          I&apos;m your assistant. Ask about your data, or tell me to set up a campaign, edit a
          configuration, or build a workflow.
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {SUGGESTIONS.map((suggestion) => (
          <Button
            key={suggestion.text}
            variant="outline"
            size="sm"
            className="rounded-full text-xs"
            onClick={() => onSuggestionClick(suggestion.text)}
            disabled={disabled}
          >
            {suggestion.emoji} {suggestion.text}
          </Button>
        ))}
      </div>
    </div>
  </div>
);

export const ChatbotPage = () => {
  usePageTitle('AI Assistant');
  const { chatId: urlChatId } = useParams<{ chatId?: string }>();
  const navigate = useNavigate();

  const {
    messages,
    isStreaming,
    streamingMessage,
    thinkingContent,
    activeTools,
    completedTools,
    pendingReportPayload,
    startNewChat,
    setMessages,
    setCurrentChatId,
  } = useChatStore();

  const { data: chatsData, isLoading: isLoadingChats } = useChats({ limit: 50 });
  const { data: chatMessagesData } = useChatMessages(urlChatId || null);
  const { mutate: deleteChat, isPending: isDeleting } = useDeleteChat();
  const { sendMessage, abortStream } = useSendMessage();

  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isFromDictationRef = useRef(false);

  const {
    isListening,
    isSupported: isDictationSupported,
    isConnecting: isDictationConnecting,
    error: dictationError,
    startListening,
    stopListening,
    resetTranscript,
  } = useDictation({
    onTranscriptChange: (text) => {
      isFromDictationRef.current = true;
      setInputValue(text);
    },
  });

  // Sync store with URL and cancel streaming when switching chats
  useEffect(() => {
    // If streaming is active and user switched chats, cancel it
    if (isStreaming) {
      abortStream();
    }
    // Sync store's currentChatId with URL
    setCurrentChatId(urlChatId || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isStreaming intentionally omitted to avoid re-running on streaming state changes
  }, [urlChatId, abortStream, setCurrentChatId]);

  // Load messages when chat changes (but not while streaming to avoid overwriting optimistic messages)
  useEffect(() => {
    if (chatMessagesData?.messages && !isStreaming) {
      setMessages(chatMessagesData.messages.map(apiMessageToUIMessage));
    }
  }, [chatMessagesData, setMessages, isStreaming, urlChatId]);

  // Scroll to bottom when messages change or streaming updates. Use instant
  // ('auto') scrolling — 'smooth' restarts an animation on every streamed token,
  // which reads as flickering. Only follow if the user is near the bottom.
  useEffect(() => {
    const el = messagesEndRef.current;
    if (!el) return;
    const scroller = el.parentElement?.parentElement; // CardContent (overflow-y-auto)
    if (scroller) {
      const nearBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 120;
      if (!nearBottom) return;
    }
    el.scrollIntoView({ behavior: 'auto' });
  }, [messages, streamingMessage, thinkingContent, activeTools, completedTools]);

  // Unconditional smooth scroll to the latest message, for discrete actions
  // (sending, opening/switching a chat) — bypasses the near-bottom guard above.
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    requestAnimationFrame(() => messagesEndRef.current?.scrollIntoView({ behavior }));
  }, []);

  // On opening or switching a chat (once its messages have loaded), go to bottom.
  useEffect(() => {
    if (!isStreaming) scrollToBottom('smooth');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on chat open / message load only
  }, [urlChatId, chatMessagesData, scrollToBottom]);

  // Auto-scroll textarea to bottom when dictation adds text
  useEffect(() => {
    if (inputRef.current && isListening) {
      inputRef.current.scrollTop = inputRef.current.scrollHeight;
    }
  }, [inputValue, isListening]);

  const handleSend = async () => {
    const input = inputValue.trim();
    if (!input || isStreaming) return;

    // Stop listening if active
    if (isListening) {
      stopListening();
    }

    // Clear input and transcript
    setInputValue('');
    resetTranscript();

    const sendPromise = sendMessage(input);
    scrollToBottom('smooth');
    await sendPromise;
  };

  const handleVoiceToggle = () => {
    if (isListening || isDictationConnecting) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // If change came from dictation, just clear the flag
    if (isFromDictationRef.current) {
      isFromDictationRef.current = false;
      setInputValue(e.target.value);
      return;
    }

    // User is typing manually - stop dictation if active
    if (isListening) {
      stopListening();
    }
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSelectChat = (chatId: string) => {
    if (chatId !== urlChatId) {
      navigate(`/chatbot/${chatId}`);
    }
  };

  const handleDeleteChat = (e: React.MouseEvent, chatId: string) => {
    e.stopPropagation();
    setChatToDelete(chatId);
  };

  const handleConfirmDelete = () => {
    if (chatToDelete) {
      deleteChat(chatToDelete);
      setChatToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setChatToDelete(null);
  };

  // Get current chat title
  const currentChat = chatsData?.chats.find((chat) => chat.id === urlChatId);
  const currentChatTitle = currentChat?.title || 'New Chat';

  const handleNewChat = () => {
    startNewChat();
    if (urlChatId) {
      navigate('/chatbot');
    }
  };

  const renderMessageContent = (content: string) => {
    return content.split('\n').map((line, i) => {
      // Handle bold text with **
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <span key={i}>
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j}>{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
          {i < content.split('\n').length - 1 && <br />}
        </span>
      );
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Assistant</p>
        <h1 className="text-4xl font-bold tracking-tight">Assistant</h1>
        <p className="text-muted-foreground max-w-3xl">
          Ask about your data, or have the assistant manage configurations, campaigns and workflows
          for you. It explains its reasoning and asks before making changes.
        </p>
      </div>

      {/* Chat Container with Sidebar */}
      <div className="flex h-150 gap-4">
        {/* Chat Sidebar */}
        <Card className="flex w-64 flex-col">
          <div className="border-b p-3">
            <Button onClick={handleNewChat} className="w-full" variant="outline" size="sm">
              <Plus className="mr-2 h-4 w-4" />
              New Chat
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {isLoadingChats ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
              </div>
            ) : chatsData?.chats.length === 0 ? (
              <div className="text-muted-foreground py-4 text-center text-sm">No chats yet</div>
            ) : (
              <div className="space-y-1">
                {chatsData?.chats.map((chat) => (
                  <div
                    key={chat.id}
                    onClick={() => handleSelectChat(chat.id)}
                    className={`group flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                      urlChatId === chat.id
                        ? 'bg-primary text-primary-foreground dark:text-white'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <MessageSquare className="h-4 w-4 shrink-0" />
                    <span className="flex-1 truncate">{chat.title || 'New Chat'}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100 ${
                        urlChatId === chat.id
                          ? 'hover:bg-primary-foreground/20'
                          : 'hover:bg-destructive/20'
                      }`}
                      onClick={(e) => handleDeleteChat(e, chat.id)}
                      disabled={isDeleting}
                    >
                      <Trash2
                        className={`h-3 w-3 ${
                          urlChatId === chat.id ? 'text-primary-foreground' : 'text-destructive'
                        }`}
                      />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Main Chat Area */}
        <Card className="flex flex-1 flex-col">
          {/* Chat Title Header */}
          <div className="border-b px-4 py-3">
            <h2 className="text-sm font-medium">{currentChatTitle}</h2>
          </div>

          {/* Messages Area */}
          <CardContent className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {/* Welcome message for new chats */}
              {messages.length === 0 && !isStreaming && (
                <WelcomeSection
                  onSuggestionClick={(suggestion) => sendMessage(suggestion)}
                  disabled={isStreaming}
                />
              )}

              {/* Chat messages */}
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start gap-3 ${
                    message.role === 'user' ? 'flex-row-reverse' : ''
                  }`}
                >
                  {/* Avatar */}
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground dark:text-white'
                        : 'bg-muted'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <User className="h-4 w-4" />
                    ) : (
                      <Bot className="h-4 w-4" />
                    )}
                  </div>

                  {/* Message Bubble */}
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground dark:text-white'
                        : 'bg-muted'
                    }`}
                  >
                    {/* Reasoning + tool calls for assistant messages */}
                    {message.role === 'assistant' && message.thinkingContent && (
                      <div className="mb-1">
                        <ReasoningDropdown text={message.thinkingContent} />
                      </div>
                    )}
                    {message.role === 'assistant' &&
                      message.toolCalls &&
                      message.toolCalls.length > 0 && (
                        <div className="mb-2">
                          <ToolCallsDropdown toolCalls={message.toolCalls} />
                        </div>
                      )}
                    <div className="text-sm whitespace-pre-wrap">
                      {renderMessageContent(message.content)}
                    </div>
                    <div
                      className={`mt-1 text-xs ${
                        message.role === 'user'
                          ? 'text-primary-foreground/70 dark:text-white/70'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {message.timestamp.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>
                </div>
              ))}

              {/* Streaming indicators */}
              {isStreaming && (
                <div className="flex items-start gap-3">
                  <div className="bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="max-w-[80%] space-y-2">
                    {/* Thinking indicator */}
                    {thinkingContent && (
                      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800 dark:bg-blue-950">
                        <div className="mb-1 flex items-center gap-2 text-blue-600 dark:text-blue-400">
                          <Brain className="h-4 w-4" />
                          <span className="text-xs font-medium">Thinking...</span>
                        </div>
                        <div className="text-muted-foreground max-h-24 overflow-y-auto text-xs">
                          {thinkingContent}
                        </div>
                      </div>
                    )}

                    {/* Tool execution indicators */}
                    {activeTools.map((tool, index) => (
                      <div
                        key={`${tool.name}-${index}`}
                        className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950"
                      >
                        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                          <Wrench className="h-4 w-4" />
                          <span className="text-xs font-medium">Running: {tool.name}</span>
                          <Loader2 className="h-3 w-3 animate-spin" />
                        </div>
                      </div>
                    ))}

                    {/* Streaming response */}
                    {streamingMessage ? (
                      <div className="bg-muted rounded-lg px-4 py-3">
                        {/* Show completed tools dropdown during streaming */}
                        {completedTools.length > 0 && (
                          <div className="mb-2">
                            <ToolCallsDropdown
                              toolCalls={completedTools.map((t) => ({
                                tool_name: t.name,
                                success: t.success,
                                formatted_text: t.formatted_text,
                              }))}
                            />
                          </div>
                        )}
                        <div className="text-sm whitespace-pre-wrap">
                          {renderMessageContent(streamingMessage)}
                        </div>
                      </div>
                    ) : (
                      !thinkingContent &&
                      activeTools.length === 0 && (
                        <div className="bg-muted rounded-lg px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-muted-foreground text-sm">Analysing...</span>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </CardContent>

          {/* PDF download (set when a create_pdf tool runs) */}
          {!!pendingReportPayload && !isStreaming && (
            <div className="px-4 pb-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => generateDataPdf(pendingReportPayload as DataPdfPayload)}
              >
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
            </div>
          )}

          {/* Input Area */}
          <div className="border-t p-4">
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={
                  isDictationConnecting
                    ? 'Connecting...'
                    : isListening
                      ? 'Listening...'
                      : 'Ask a question about your data...'
                }
                className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[44px] w-full resize-none rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                rows={1}
                disabled={isStreaming}
              />
              {/* Action button: Mic when empty, Send when has text or listening */}
              {inputValue.trim() || isListening || isDictationConnecting ? (
                <Button
                  onClick={isListening || isDictationConnecting ? handleVoiceToggle : handleSend}
                  disabled={isStreaming}
                  size="icon"
                  className="h-[44px] w-[44px] shrink-0"
                >
                  {isStreaming ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isListening || isDictationConnecting ? (
                    isDictationConnecting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mic className="h-4 w-4" />
                    )
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleVoiceToggle}
                  disabled={isStreaming || !isDictationSupported}
                  size="icon"
                  className="h-[44px] w-[44px] shrink-0"
                  variant="outline"
                  title={
                    isDictationSupported
                      ? 'Click to start voice input'
                      : 'Voice input not supported in this browser'
                  }
                >
                  <Mic className="h-4 w-4" />
                </Button>
              )}
            </div>
            {dictationError ? (
              <p className="mt-2 text-xs text-red-500">{dictationError}</p>
            ) : (
              <p className="text-muted-foreground mt-2 text-xs">
                Press Enter to send, Shift+Enter for new line
              </p>
            )}
          </div>
        </Card>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationDialog
        open={chatToDelete !== null}
        resourceName="chat"
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};
